/* eslint-disable react/jsx-handler-names */
/* eslint-disable max-lines */
/* eslint-disable react/jsx-boolean-value */
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { calDiscountPercentage, debounce, getProductLargeImgList } from "../../../utils/common";
import CustomImage from "../../atoms/customImage";
import ProductCardImageSlider from "../../molecules/productCardImageslider";
import ProductInfo from "./productInfo";
import { PLP_PRODUCT_VIDEO_POSITION, plpCleverTapEvents } from "../../../constants/pageConstants/plpConstants";
import { COUNTRIES, fnpPageType } from "../../../constants/common/common";
import { getProductVideoUrl } from "../../../utils/pdp/pdpWidgetUtils";
import { getEarliestDeliveryText, getImpressionDeliveryMeta } from "../../../utils/plp/serviceabilityDelivery";
import { shallowEqual, useSelector } from "react-redux";
import { checkIsInternationalProduct } from "../../../utils/pdp/nonDeliverable";
import useAnalytics from "../../../analytics/useAnalytics";
import { campaignEvents, cleverTapCommonEvents, cleverTapCommonWords } from "../../../analytics/clevertapConstants";
import { useWidgetClick } from "../../../utils/dynamicHomeUtils/WidgetClickContext";
import { usePathname } from "next/navigation";
import VideoPlayer from "../../atoms/videoPlayer";
import ProductCardSkeleton from "../../molecules/skeletons/productCardSkeleton";

/** */
const ProductCard = ({
  image,
  productUrl,
  title,
  price,
  productId,
  productAttributes,
  imgHeight,
  imgWidth,
  review,
  imageCount = 1,
  urlIdentifier = "",
  category,
  isPriority = false,
  isMobile,
  productWidget,
  skuCode,
  productCount,
  isHideSavedPercent = false,
  widgetTitle,
  isSmallReviewTag = false,
  earliestDelivery,
  deliveryEntry,
  experiment,
  isFromSearch = false,
  pincode,
  isPlpRevampTiles,
  isLoaded = true,
  trackDynamicHomeWidgetClick = false,
  serverSelectedCurrency = null,
  isShowRatingWhenAvailable = false,
  isAutoPlay = false,
  isLoop = false,
  isPlayVideoOnCardHover = false,
  isFromGiftFinder = false,
  isDeliveryTimeHidden = false,
}) => {
  const pathname = usePathname();
  const { onCellClick: onDynamicHomeWidgetClick } = useWidgetClick() ?? {};
  const [productImgList, setProductImgList] = useState([]);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true); // Set to true after component mounts (on client)
  }, []);
  let catalogId = "";

  // Extract catalogId and categoryId from urlIdentifier and productUrl if present
  if (urlIdentifier) {
    catalogId = urlIdentifier.split("/")?.[0];
  }
  const sliderRef = useRef(null);
  const { trackEvent } = useAnalytics();

  const { selectedCurrencyState, deliveryDates, pinCode, addRess, selectedIntLocation, infiniteLoader } = useSelector(
    (state) => ({
      selectedCurrencyState: state?.currencyList?.userSelectedCurrency?.currency?.id,
      deliveryDates: state?.deliveryDate?.details.deliveryDates,
      pinCode: state?.locationLock?.selectedDomesticLocation?.pincode,
      addRess: state?.locationLock?.selectedDomesticLocation?.address,
      selectedIntLocation: state?.locationLock.selectedIntLocation,
      infiniteLoader: state?.cart?.isLoaderVisible,
    }),
    shallowEqual,
  );
  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");
  const isSearch = pathname?.includes("/search");
  const selectedCurrency = serverSelectedCurrency?.currency?.id || selectedCurrencyState;
  const discountPercentage = calDiscountPercentage(price?.mrp, price?.listPrice);

  /**
   *
   * @param {string} url that we are passing on click of the product card
   * @returns the position value from the product url.
   */
  function getPosFromUrl(url) {
    const queryString = url.split("?")[1] || "";
    const params = new URLSearchParams(queryString);
    return params?.get("pos");
  }

  /**return Page Type */
  const returnPageType = () => {
    if (isHome) {
      return cleverTapCommonWords?.HOME;
    }
    if (isFromSearch) {
      return cleverTapCommonWords?.AutoSuggestions;
    }
    if (pathname?.includes("/santa")) {
      return cleverTapCommonWords?.SANTA;
    }
    return cleverTapCommonWords?.PDP;
  };

  const cleverTapData = {
    widget_name: widgetTitle || "",
    page_type: returnPageType(),
    page_name: productWidget?.urlIdentifier || "",
    is_carousel: productCount > 3,
    is_having_product: productCount > 0,
    is_having_image: true,
    is_having_cta: false,
    product_name: productWidget?.productName,
    pid: productWidget?.productId,
    product_category: productWidget?.primaryCategoryId,
    product_position: productWidget?.pos,
    product_row: 1,
    country: catalogId,
    is_international: catalogId !== COUNTRIES.INDIA,
    city: catalogId !== COUNTRIES.INDIA ? selectedIntLocation?.location : "",
    area: catalogId !== COUNTRIES.INDIA ? "" : addRess,
    pincode: catalogId !== COUNTRIES.INDIA ? "" : pinCode,
    system_pincode: catalogId !== COUNTRIES.INDIA ? "" : pinCode,
    sku_id: skuCode,
  };

  const productVideoUrl = getProductVideoUrl(productWidget);
  const shouldPlayVideoOnCardHover = Boolean(productVideoUrl) && (isPlayVideoOnCardHover || isPLP || isSearch);

  useEffect(() => {
    const images = getProductLargeImgList(image?.path || image?.url || image?.imageUrl, imageCount);
    if (productVideoUrl) {
      images.splice(PLP_PRODUCT_VIDEO_POSITION, 0, productVideoUrl);
    }
    setProductImgList(images);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageCount, image, productVideoUrl]);

  /**
   * Handles mouse enter event to start the slider autoplay.
   * This function will only start the autoplay if the device is not mobile and the sliderRef is available.
   *
   * @function onHandleMouseEnter
   * @returns {void} No return value.
   */
  const onHandleMouseEnter = useCallback(() => {
    if (isMobile || !sliderRef.current) {
      return;
    }

    if (shouldPlayVideoOnCardHover) {
      sliderRef.current.playVideo?.();
      return;
    }

    sliderRef.current.startAutoplay();
  }, [isMobile, shouldPlayVideoOnCardHover]);

  /**
   * Handles mouse leave event to stop the slider autoplay or pause video.
   *
   * @function onHandleMouseLeave
   * @returns {void}
   */
  const onHandleMouseLeave = useCallback(() => {
    if (isMobile || !sliderRef.current) {
      return;
    }

    if (shouldPlayVideoOnCardHover) {
      sliderRef.current.pauseVideo?.();
      return;
    }

    sliderRef.current.stopAutoplay();
  }, [isMobile, shouldPlayVideoOnCardHover]);

  /**
   * On Product Click to track clevertap events.
   */
  const onProductClick = debounce(() => {
    const resolvedDeliveryEntry = deliveryDates?.[productId] ?? deliveryEntry;
    const { deliveryType, deliveryTime } = getImpressionDeliveryMeta(resolvedDeliveryEntry);

    const eventProperties = {
      page_name: pathname,
      page_type: fnpPageType.CATEGORY,
      pid: productId,
      product_name: title,
      product_category: category,
      product_price: price?.listPrice,
      currency: selectedCurrency,
      strike_price: price?.mrp,
      discount_percentage: discountPercentage,
      product_rating: review?.ratingValue,
      product_review_count: review?.ratingCount,
      earliest_delivery_date:
        getEarliestDeliveryText(deliveryDates?.[productId]) ||
        getEarliestDeliveryText(deliveryEntry) ||
        earliestDelivery,
      delivery_type: deliveryType,
      delivery_time: deliveryTime,
      is_international: checkIsInternationalProduct(category),
      product_tag: productAttributes
        ?.map((tag) => tag?.description)
        .filter(Boolean)
        .join(", "),
      product_position: getPosFromUrl(productUrl),
      is_plp_video_available: Boolean(productVideoUrl),
      clean_url: pathname,
      experiment_type: experiment?.campaignId ? experiment?.campaignId : "",
      user_group: experiment?.groupName ? experiment?.groupName : "",
      segment_id: experiment?.segmentId ? experiment?.segmentId : "",
      // Conditional so every non-gift-finder call site keeps an identical payload.
      ...(isFromGiftFinder ? { is_from_gift_finder: true } : {}),
    };
    const cdpData = {
      page_type: eventProperties?.page_type || cleverTapData?.page_type || "",
      page_name: eventProperties?.page_name || cleverTapData?.page_name || "",
      experiment_type: experiment?.campaignId ? experiment?.campaignId : "",
      user_group: experiment?.groupName ? experiment?.groupName : "",
      segment_id: experiment?.segmentId ? experiment?.segmentId : "",
      products: [
        {
          productId: eventProperties?.pid || "",
          city: cleverTapData?.pid || "",
          area: cleverTapData?.area || "",
          pincode: cleverTapData?.pincode || "",
          systemPincode: cleverTapData?.system_pincode || "",
          country: cleverTapData?.country,
          productCategory: eventProperties?.product_category || cleverTapData?.product_category || "",
          productName: eventProperties?.product_name || cleverTapData?.product_name || "",
          skuId: skuCode || "",
          productRating: eventProperties?.product_rating || "",
          productReviewCount: eventProperties?.product_review_count || "",
          productPrice: eventProperties?.product_price || "",
          currency: eventProperties?.currency || "",
          strikePrice: eventProperties?.strike_price || "",
          discountPercentage: eventProperties?.discount_percentage || "",
          isInternational: eventProperties?.is_international || cleverTapData?.is_international || "",
          position: eventProperties?.product_position || "",
          delivery_type: deliveryType,
          delivery_time: deliveryTime,
        },
      ],
    };

    // The gift finder results route is a listing page in every respect except its
    // pathname, which is what isPLP/isSearch sniff — so it opts in explicitly rather
    // than falling through to the widget-click branch below.
    if (isSearch || isPLP || isFromGiftFinder) {
      trackEvent(plpCleverTapEvents.PRODUCT_CLICKED, eventProperties, ["clevertap"]);
      trackEvent(plpCleverTapEvents.PRODUCT_CLICKED, cdpData, ["CDP"]);
    } else if (trackDynamicHomeWidgetClick && onDynamicHomeWidgetClick) {
      onDynamicHomeWidgetClick({
        action: "Product Clicked",
        item: {
          pid: productId,
          product_category: productWidget?.primaryCategoryId,
          sku_id: skuCode,
          redirection_url: productUrl,
          item_name: title,
          item_position: productWidget?.pos,
          item_row: 1,
          item_column: (productWidget?.pos ?? 0) + 1,
          is_international: checkIsInternationalProduct(category) ? "Y" : "N",
        },
      });
    } else {
      trackEvent(cleverTapCommonEvents.widgetClicked, { ...cleverTapData, flow: "new" }, ["clevertap"]);
      trackEvent(plpCleverTapEvents.PRODUCT_CLICKED, cdpData, ["CDP"]);
    }
    if (productWidget && productWidget?.campaign_details) {
      trackEvent(campaignEvents.RENDER_NOTIFICATION_CLICKED, productWidget?.campaign_details, ["clevertap"]);
    }
  }, 50);

  return (
    <div
      onClick={onProductClick}
      onMouseEnter={!isMobile && !isPlayVideoOnCardHover ? onHandleMouseEnter : undefined}
      onMouseLeave={!isMobile && !isPlayVideoOnCardHover ? onHandleMouseLeave : undefined}
      data-productid={productId}
      itemProp="offers"
      itemScope
      itemType="http://schema.org/Offer"
      className="h-full"
    >
      {isMounted && !isLoaded && !infiniteLoader ? (
        <div className="flex h-full w-full">
          <div
            className={`relative w-full overflow-hidden ${isMobile ? "rounded-t-lg" : isPlpRevampTiles ? "rounded-2xl" : "rounded-lg"} sm:border sm:border-gray-100 sm:bg-white-900`}
          >
            <ProductCardSkeleton />
          </div>
        </div>
      ) : (
        <a
          href={productUrl}
          className="flex h-full w-full"
          itemProp="url"
          rel={isMobile ? null : "noopener"}
          target={isMobile ? null : "_blank"}
        >
          <div
            className={`relative w-full overflow-hidden md:hover:shadow-[0_0_24px_0_rgba(0,0,0,0.12)] ${isMobile ? "rounded-t-lg" : isPlpRevampTiles ? "rounded-2xl" : "rounded-lg"} duration-300 ease-in-out hover:border-0 sm:border sm:border-gray-100 sm:bg-white-900 sm:hover:border sm:hover:border-radioUncheck`}
          >
            <meta itemProp="availability" content={`https://schema.org/InStock`} />

            <div
              className="relative"
              onMouseEnter={!isMobile && isPlayVideoOnCardHover ? onHandleMouseEnter : undefined}
              onMouseLeave={!isMobile && isPlayVideoOnCardHover ? onHandleMouseLeave : undefined}
            >
              {isMobile ? (
                productVideoUrl ? (
                  <>
                    <VideoPlayer
                      videoUrl={productVideoUrl}
                      preload="none"
                      aria-label="Video player"
                      id={productId}
                      isMuted={true}
                      poster={image?.path}
                      width={imgWidth}
                      height={imgHeight}
                      classes="rounded-8"
                      isPlaysInline={true}
                      isAutoPlay={isAutoPlay}
                      isLoop={isLoop}
                    />
                    <div className="hidden">
                      <CustomImage
                        src={!isMounted ? "/icons/placeholder-bb.png" : image?.path}
                        width={imgWidth}
                        height={imgHeight}
                        alt={image?.altText ? image?.altText : image?.alt}
                        borderRadius={8}
                        isPriority={!isMounted ? true : isPriority}
                        itemProp="image"
                        sizes="(max-width: 768px) 100vw, 50vw"
                      />
                    </div>
                  </>
                ) : (
                  <CustomImage
                    src={!isMounted ? "/icons/placeholder-bb.png" : image?.path}
                    width={imgWidth}
                    height={imgHeight}
                    alt={image?.altText ? image?.altText : image?.alt}
                    borderRadius={8}
                    isPriority={!isMounted ? true : isPriority}
                    itemProp="image"
                    sizes="(max-width: 768px) 100vw, 50vw"
                  />
                )
              ) : (
                <ProductCardImageSlider
                  defaultImg="/icons/placeholder-bb.png"
                  productImgList={productImgList}
                  imgHeight={imgHeight}
                  imgWidth={imgWidth}
                  ref={sliderRef}
                  isPriority={isPriority}
                  alt={image?.altText ? image?.altText : image?.alt}
                  isMounted={isMounted}
                  productId={productId}
                  isPLP={isPLP}
                  isSearch={isSearch}
                  videoUrl={productVideoUrl}
                  isPlayVideoOnCardHover={isPlayVideoOnCardHover}
                />
              )}
            </div>
            <ProductInfo
              title={title}
              price={price}
              productId={productId}
              isHideSavedPercent={isHideSavedPercent}
              isSmallReviewTag={isSmallReviewTag}
              review={review}
              productAttributes={productAttributes}
              isMobile={isMobile}
              deliveryEntry={deliveryEntry}
              earliestDelivery={earliestDelivery}
              pincode={pincode}
              isPlpRevampTiles={isPlpRevampTiles}
              serverSelectedCurrency={serverSelectedCurrency}
              isShowRatingWhenAvailable={isShowRatingWhenAvailable}
              isDeliveryTimeHidden={isDeliveryTimeHidden}
            />
          </div>
        </a>
      )}
    </div>
  );
};

ProductCard.propTypes = {
  image: PropTypes.shape({
    path: PropTypes.string.isRequired,
    imageUrl: PropTypes.string,
    url: PropTypes.string,
    altText: PropTypes.string,
    alt: PropTypes.string,
  }).isRequired,
  title: PropTypes.string.isRequired,
  price: PropTypes.shape({
    listPrice: PropTypes.number,
    mrp: PropTypes.number,
    percentSaved: PropTypes.number,
  }).isRequired,
  productId: PropTypes.string.isRequired,
  productUrl: PropTypes.string.isRequired,
  productAttributes: PropTypes.arrayOf(PropTypes.object),
  imgHeight: PropTypes.number,
  imgWidth: PropTypes.number,
  review: PropTypes.object,
  imageCount: PropTypes.number,
  urlIdentifier: PropTypes.string.isRequired,
  category: PropTypes.string,
  isPriority: PropTypes.bool,
  isMobile: PropTypes.bool,
  productWidget: PropTypes.object,
  skuCode: PropTypes.string,
  productCount: PropTypes.number,
  isHideSavedPercent: PropTypes.bool,
  widgetTitle: PropTypes.string,
  isSmallReviewTag: PropTypes.bool,
  earliestDelivery: PropTypes.string,
  deliveryEntry: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  experiment: PropTypes.object,
  isFromSearch: PropTypes.bool,
  /** PLP: delivery tag shows day/date only (deliveryTimeVisibility A/B). */
  isDeliveryTimeHidden: PropTypes.bool,
  pincode: PropTypes.string,
  isPlpRevampTiles: PropTypes.bool,
  isLoaded: PropTypes.bool,
  /** When true (dynamic home product rails), fires Widget Clicked with action Product Clicked via context. */
  // eslint-disable-next-line react/boolean-prop-naming
  trackDynamicHomeWidgetClick: PropTypes.bool,
  serverSelectedCurrency: PropTypes.object,
  isShowRatingWhenAvailable: PropTypes.bool,
  isAutoPlay: PropTypes.bool,
  isLoop: PropTypes.bool,
  isPlayVideoOnCardHover: PropTypes.bool,
  isFromGiftFinder: PropTypes.bool,
};

export default React.memo(ProductCard);
